"use client"
import { FormTitle } from "@/components/Inputs"
import { Calendar } from "@/components/SVGs"
import { Colors } from "@/components/Theme/colors"
import { Fonts } from "@/components/Theme/font"
import Resizer from "react-image-file-resizer"

export const numberFormatter = (value) => {
  const formatValue = (num, divisor, suffix) => {
    const divided = num / divisor
    return divided % 1 === 0
      ? divided.toLocaleString() + suffix
      : divided.toLocaleString(undefined, { minimumFractionDigits: 2 }) + suffix
  }
  if (value >= 1000000000) {
    return formatValue(value, 1000000000, "B")
  } else if (value >= 1000000) {
    return formatValue(value, 1000000, "M")
  } else if (value >= 1000) {
    return formatValue(value, 1000, "K")
  }
  return value?.toLocaleString() // Format small numbers with commas
}
// Helper function: convert a column number to its Excel letter (supports >26 columns)
export const getExcelColumnLetter = (colNum) => {
  let dividend = colNum
  let columnName = ""
  let modulo
  while (dividend > 0) {
    modulo = (dividend - 1) % 26
    columnName = String.fromCharCode(65 + modulo) + columnName
    dividend = Math.floor((dividend - modulo) / 26)
  }
  return columnName
}

export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
// Converts HEX to HSL, ignoring the alpha component
export const hexToHsl = (hex) => {
  // Remove the '#' at the beginning if present
  hex = hex.replace(/^#/, "")

  // If the hex value contains alpha (last two characters), remove them
  if (hex.length === 8) {
    hex = hex.substring(0, 6) // Remove the alpha channel
  }

  // Parse the RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  // Find the maximum and minimum RGB values
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  // Calculate the hue
  let h = 0
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta) % 6
    } else if (max === g) {
      h = (b - r) / delta + 2
    } else {
      h = (r - g) / delta + 4
    }
  }

  // Convert hue to degrees
  h = Math.round(h * 60)
  if (h < 0) h += 360

  // Calculate the lightness
  let l = (max + min) / 2

  // Calculate the saturation
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
  }

  // Convert saturation and lightness to percentage
  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return `hsl(${h}, ${s}%, ${l}%)`
}

// Dynamic colors generation using HSL
export const dynamicColors = (index, colorLayer) => {
  const color = colorLayer[index % colorLayer.length] // Use colorLayer to define color cycles
  return hexToHsl(color) // Return the color from the layer
}

export const getTextColor = (index, colorLayer) => {
  const bgColor = dynamicColors(index, colorLayer)
  const match = bgColor.match(/(\d+)%\)$/) // Extract lightness from HSL color
  if (!match) {
    return "#101828" // Fallback to black
  }
  const lightness = parseInt(match[1], 10)
  return lightness > 50 ? "#101828" : "#FFFFFF" // Dark text on light background, light text on dark background
}
export const currencies = [
  { currency: "Nigerian Naira (₦)", code: "NGN", locale: "en-NG" },
  { currency: "US Dollar ($)", code: "USD", locale: "en-US" },
  { currency: "Euro (€)", code: "EUR", locale: "fr-FR" },
  { currency: "British Pound (£)", code: "GBP", locale: "en-GB" },
  { currency: "Chinese Yuan (¥)", code: "CNY", locale: "zh-CN" },
  { currency: "Canadian Dollar (C$)", code: "CAD", locale: "en-CA" },
  { currency: "Japanese Yen (¥)", code: "JPY", locale: "ja-JP" },
  { currency: "Indian Rupee (₹)", code: "INR", locale: "hi-IN" },
  { currency: "Brazilian Real (R$)", code: "BRL", locale: "pt-BR" },
  { currency: "South Korean Won (₩)", code: "KRW", locale: "ko-KR" },
  { currency: "Mexican Peso (MX$)", code: "MXN", locale: "es-MX" },
  { currency: "Russian Ruble (₽)", code: "RUB", locale: "ru-RU" },
  { currency: "Turkish Lira (₺)", code: "TRY", locale: "tr-TR" },
  { currency: "Indonesian Rupiah (Rp)", code: "IDR", locale: "id-ID" },
  { currency: "Thai Baht (฿)", code: "THB", locale: "th-TH" },
  { currency: "Vietnamese Dong (₫)", code: "VND", locale: "vi-VN" },
  { currency: "Polish Zloty (zł)", code: "PLN", locale: "pl-PL" },
  { currency: "Saudi Riyal (ر.س)", code: "SAR", locale: "ar-SA" },
]
export const currencyDisplayConfig = {
  NGN: { symbol: "₦", position: "prefix" }, // Nigerian Naira
  USD: { symbol: "$", position: "prefix" }, // US Dollar
  EUR: { symbol: "€", position: "suffix" }, // Euro (used as suffix in most European locales)
  GBP: { symbol: "£", position: "prefix" }, // British Pound
  CNY: { symbol: "¥", position: "prefix" }, // Chinese Yuan
  JPY: { symbol: "¥", position: "prefix" }, // Japanese Yen
  INR: { symbol: "₹", position: "prefix" }, // Indian Rupee
  CAD: { symbol: "C$", position: "prefix" }, // Canadian Dollar
  AUD: { symbol: "A$", position: "prefix" }, // Australian Dollar
  BRL: { symbol: "R$", position: "prefix" }, // Brazilian Real
  KRW: { symbol: "₩", position: "prefix" }, // South Korean Won
  MXN: { symbol: "MX$", position: "prefix" }, // Mexican Peso
  RUB: { symbol: "₽", position: "suffix" }, // Russian Ruble (often used as suffix)
  TRY: { symbol: "₺", position: "prefix" }, // Turkish Lira
  IDR: { symbol: "Rp", position: "prefix" }, // Indonesian Rupiah
  THB: { symbol: "฿", position: "prefix" }, // Thai Baht
  VND: { symbol: "₫", position: "suffix" }, // Vietnamese Dong (often suffix)
  PLN: { symbol: "zł", position: "suffix" }, // Polish Zloty
  SAR: { symbol: "ر.س", position: "suffix" }, // Saudi Riyal
  ZAR: { symbol: "R", position: "prefix" }, // South African Rand
}
export function formatCurrency({ value, currency = "NGN", locale = "en-NG" }) {
  const cleanValue =
    typeof value === "string" ? Number(value?.replace(/,/g, "")) : value ?? 0

  const config = currencyDisplayConfig[currency] || {
    symbol: currency,
    position: "prefix",
  }

  const formatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(cleanValue)

  return config?.position === "prefix"
    ? `${config?.symbol}${formatted}`
    : `${formatted} ${config?.symbol}`
}
//FETCH LOCATION COORDINATES
export const fetchCoordinates = async (params) => {
  try {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY
    const query = encodeURIComponent(params)
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${apiKey}`
    const response = await fetch(url)
    const data = await response.json()
    if (data.status === "OK" && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location
      return [lat, lng]
    } else {
      return null
    }
  } catch (error) {
    console.error(
      "Error fetching coordinates from Google Geocoding API:",
      error
    )
    return null
  }
}
export const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) resolve(reader.result.toString())
      else reject("Failed to convert Blob to base64")
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
// Function to convert data URI to Blob
export const dataURItoBlob = (dataURI) => {
  const byteString = atob(dataURI.split(",")[1])
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0]

  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }

  return new Blob([ab], { type: mimeString })
}

export const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    Resizer.imageFileResizer(
      file,
      200, // width
      200, // height
      "JPEG", // format
      100, // quality
      0, // rotation
      (uri) => {
        const blob = dataURItoBlob(uri) // Convert the resized image to Blob

        // Convert Blob to File
        const resizedFile = new File(
          [blob],
          file.name.replace(/\.[^/.]+$/, "") + "-resized.jpeg",
          {
            type: blob.type,
          }
        )

        resolve(resizedFile) // Resolve with the File
      },
      "base64" // Set output type as base64
    )
  })
}
// Put this helper just above your component or inside it:
export const formatCellValue = (value) => {
  // 1. empty → dash
  if (value === null || value === undefined || value === "") return "-"

  // 2. non‑numeric → string as‑is
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  // 3. year detection: four‑digit, integer, plausible range
  if (
    Number.isInteger(num) &&
    num >= 1900 &&
    num <= 2100 &&
    String(num).length === 4
  ) {
    return num // show “2025”, not “2,025.00”
  }

  // 4. every other number → locale formatted with two decimals
  return num.toLocaleString(undefined, { minimumFractionDigits: 2 })
}
export const formatString = (string) => {
  return string
    ?.replace(/\\n/g, "\n")
    .split("\n")
    .map((line, idx) => (
      <p key={idx} className="mt-b">
        {line}
        {idx < string?.split("\n").length - 1 && <br />}
      </p>
    ))
}
export const RevExDisplay = ({ proposed, approved }) => (
  <div className="flex flex-col justify-between">
    <p className="font-medium font-inter text-[#333333] flex gap-1">
      Proposed:{" "}
      <span className="text-error">{`₦${numberFormatter(proposed)}`}</span>
    </p>
    <p className="font-medium font-inter text-[#333333] flex gap-1">
      Approved:{" "}
      <span className="text-primary">{`₦${numberFormatter(approved)}`}</span>
    </p>
  </div>
)

export const ProposalCol = ({ proposed, recommended, approved, actual }) => (
  <div className="w-full flex justify-between gap-6">
    <div className="flex flex-col gap-1">
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Proposed:{" "}
        </p>
        <span className="text-error font-bold font-inter">{`₦${numberFormatter(
          proposed
        )}`}</span>
      </div>
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Recommended:{" "}
        </p>
        <span className="text-warning font-bold font-inter">{`₦${numberFormatter(
          recommended
        )}`}</span>
      </div>
    </div>
    <div className="flex flex-col gap-1 justify-end items-end">
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Approved:{" "}
        </p>
        <span className="text-primary font-bold font-inter">{`₦${numberFormatter(
          approved
        )}`}</span>
      </div>
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Actual:{" "}
        </p>
        <span className="text-primary_darker font-bold font-inter">{`₦${numberFormatter(
          actual
        )}`}</span>
      </div>
    </div>
  </div>
)
export const ProjectCol = ({ total, appropriated, release, spent }) => (
  <div className="w-full flex justify-between gap-6">
    <div className="flex flex-col gap-1">
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Total cost:{" "}
        </p>
        <span className="text-error font-bold font-inter">{`₦${numberFormatter(
          total
        )}`}</span>
      </div>
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Appropriated:{" "}
        </p>
        <span className="text-warning font-bold font-inter">{`₦${numberFormatter(
          appropriated
        )}`}</span>
      </div>
    </div>
    <div className="flex flex-col gap-1 justify-end items-end">
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Released:{" "}
        </p>
        <span className="text-primary font-bold font-inter">{`₦${numberFormatter(
          release
        )}`}</span>
      </div>
      <div className="w-full flex gap-4">
        <p className="w-full font-semibold font-inter text-[#333333]">
          Expended:{" "}
        </p>
        <span className="text-primary_darker font-bold font-inter">{`₦${numberFormatter(
          spent
        )}`}</span>
      </div>
    </div>
  </div>
)
export const ProgrammeCol = ({ total, gov, partner }) => (
  <div className="w-full flex justify-between gap-6">
    <div className="flex flex-col gap-1">
      <div className="w-full flex gap-4">
        <div className="flex justify-between gap-4">
          <p className="w-full font-semibold font-inter text-[#333333]">
            Total cost:{" "}
          </p>
          <span className="text-primary font-bold font-inter">{`₦${numberFormatter(
            total
          )}`}</span>
        </div>
        <div className="flex justify-between gap-4"></div>
      </div>
      <div className="w-full flex gap-4">
        <div className="flex justify-between gap-4">
          <p className="w-full font-semibold font-inter text-[#333333]">
            Government Input:{" "}
          </p>
          <span className="text-[#333333] font-bold font-inter">{`₦${numberFormatter(
            gov
          )}`}</span>
        </div>
        <div className="flex justify-between gap-4">
          <p className="w-full font-semibold font-inter text-[#333333]">
            Partner Input:{" "}
          </p>
          <span className="text-[#333333] font-bold font-inter">{`₦${numberFormatter(
            partner
          )}`}</span>
        </div>
      </div>
    </div>
  </div>
)
export const getStatusStyles = (status) => {
  switch (status) {
    case "approved":
      return "w-fit min-w-[100px] gap-[2px] py-2 px-2 rounded-[20px] bg-[#E0FCEF] text-primary text-xs tracking-[1.2px] font-semibold font-inter uppercase flex justify-center items-center"
    case "implementing":
      return "w-fit min-w-[100px] gap-[2px] py-2 px-2 rounded-[20px] bg-warning/10 text-warning text-xs tracking-[1.2px] font-semibold font-inter uppercase flex justify-center items-center"
    default:
      return "w-fit min-w-[100px] gap-[2px] py-2 px-2 rounded-[20px] bg-[#F7F7F9] text-[#808080] text-xs tracking-[1.2px] font-semibold font-inter uppercase flex justify-center items-center border border-solid border-[#E9E9E9]"
  }
}

export const slots = {
  openPickerIcon: () => (
    <Calendar
      style={{
        width: 20,
        height: 20,
        color: "#808080",
      }}
    />
  ),
}
export const dateSlotProps = {
  textField: {
    size: "small",
    fullWidth: true,
  },
  dialog: {
    sx: {
      "& .MuiPaper-root": {
        gap: "24px",
        borderRadius: 1,
        border: `1px solid ${Colors.secondaryLight}`,
        boxShadow: "none",
      },
    },
  },
  popper: {
    sx: {
      "& .MuiPaper-root": {
        gap: "24px",
        borderRadius: 1,
        border: `1px solid ${Colors.secondaryLight}`,
        boxShadow: "none",
      },
    },
  },
  calendarHeader: {
    sx: {
      "& .MuiPickersCalendarHeader-root": {
        display: "flex",
        flexDirection: "column",
      },
      "& .MuiPickersCalendarHeader-label": {
        font: `normal normal normal 600 14px/24px ${Fonts.primary}`,
        color: Colors.secondary,
        paddingRight: "7px",
        textAlign: "center",
        order: 3,
      },
      "& .MuiPickersCalendarHeader-labelContainer": {
        display: "flex",
        alignItems: "center",
        justifyItems: "center",
      },
      "& .MuiPickersCalendarHeader-labelContainer:first-of-type": {
        order: 0,
        paddingRight: "20px",
        paddingLeft: "20px",
      },
      "& .MuiPickersCalendarHeader-switchViewButton": {
        display: "none",
      },
      "& .MuiPickersCalendarHeader-switcherButton": {
        position: "relative",
        right: 200,
      },

      "& .MuiPickersFadeTransitionGroup-root": {
        display: "flex",
        position: "absolute",
        left: 110,
      },
      "& .MuiPickersArrowSwitcher-root": {
        marginLeft: "-280px",
      },

      "& .MuiPickersArrowSwitcher-spacer": {
        width: "232px",
      },
    },
  },
  day: {
    sx: {
      ":not(.Mui-selected)": {
        padding: "2px 4px",
        borderRadius: "4px",
        font: `normal normal normal 400 14px/24px ${Fonts.primary}`,
        color: Colors.secondary,
        backgroundColor: Colors.light,
        borderColor: Colors.light,
      },
      "&.MuiPickersDay-root.Mui-selected": {
        padding: "2px 4px",
        borderRadius: "4px",
        backgroundColor: Colors.primary,
        borderColor: Colors.primary,
        font: `normal normal normal 700 14px/24px ${Fonts.primary}`,
        color: Colors.light,
        ":hover": {
          color: Colors.light,
          backgroundColor: Colors.primary,
        },
      },
      ":hover": {
        color: Colors.light,
        backgroundColor: Colors.primary,
      },
      "&.MuiDayCalendar-weekDayLabel": {
        padding: "2px 4px",
        borderRadius: "4px",
        font: `normal normal normal 700 14px/24px ${Fonts.primary} !important`,
        color: Colors.secondary,
      },
      "& .MuiPickersDay-today": {
        padding: "2px 4px",
        borderRadius: "4px",
        backgroundColor: Colors.light,
        borderColor: Colors.primary,
        font: `normal normal normal 700 14px/24px ${Fonts.primary}`,
        color: Colors.secondary,
      },
    },
  },
}

export function CategoryCurrencyCards({ detail }) {
  const categories = [
    { key: "govtInput", label: "Govt Input", values: detail?.govtInput || [] },
    {
      key: "govtRelease",
      label: "Govt Release",
      values: detail?.govtRelease || [],
    },

    {
      key: "partnerInput",
      label: "Partner Input",
      values: detail?.partnerInput || [],
    },
    {
      key: "partnerRelease",
      label: "Partner Release",
      values: detail?.partnerRelease || [],
    },
    {
      key: "totalReleased",
      label: "Total Released",
      values: detail?.totalReleased || [],
    },
    {
      key: "appropriated",
      label: "Appropriated",
      values: detail?.appropriated || [],
    },
    {
      key: "totalExpended",
      label: "Total Expended",
      values: detail?.totalExpended || [],
    },
  ]

  return (
    <div className="space-y-2">
      {categories.map((cat) => {
        const textValues =
          cat.values.length > 0
            ? cat.values
                .map((item) =>
                  formatCurrency({
                    value:
                      typeof item.amount === "string"
                        ? Number(item.amount.replace(/,/g, ""))
                        : item.amount,
                    currency: item.currency || "NGN",
                    locale: item.locale || "en-NG",
                  })
                )
                .join(", ")
            : formatCurrency({ value: 0 })
        return (
          <div
            key={cat.key}
            className="bg-white border border-[#EAECF0] shadow-[0px_1px_2px_0px_#1018280D] p-4 rounded-xl"
          >
            <FormTitle className="text-left text-[#232325] font-inter font-bold text-base">
              {cat.label}
            </FormTitle>
            <p className="mt-2 font-inter font-medium text-sm/[24px] text-[#101828]">
              {textValues}
            </p>
          </div>
        )
      })}
    </div>
  )
}
